import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, catchError, map, tap } from 'rxjs';
import { ENVIRONMENT_TOKEN } from '../../app.config';
import { AuthService } from './auth.service';

export type ThemeMode = 'dark' | 'light';

export interface UserPreferences {
  preferred_scheine: number[] | null;
  theme_mode: ThemeMode | null;
  hide_mastered_questions: boolean;
}

type PreferencesUpdatePayload = {
  preferred_scheine?: number[] | null;
  theme_mode?: ThemeMode | null;
  hide_mastered_questions?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private apiUrl: string;
  private prefs$ = new BehaviorSubject<UserPreferences | null>(null);

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    @Inject(ENVIRONMENT_TOKEN) private environment: any
  ) {
    this.apiUrl = this.environment.apiUrl;
  }

  getPreferences(): Observable<UserPreferences | null> {
    return this.prefs$.asObservable();
  }

  getCurrentPreferences(): UserPreferences | null {
    return this.prefs$.getValue();
  }

  loadPreferences(): Observable<UserPreferences> {
    const cached = this.readLocal();
    if (cached) {
      this.prefs$.next(cached);
    }
    return this.http.get<UserPreferences>(`${this.apiUrl}/users/preferences.php`).pipe(
      map((res) => this.normalizeResponse(res)),
      tap((normalized) => {
        this.prefs$.next(normalized);
        this.writeLocal(normalized);
      }),
      catchError((err) => {
        console.error('[Preferences] Laden fehlgeschlagen, nutze lokalen Fallback', err);
        const fallback: UserPreferences = cached ?? { preferred_scheine: null, theme_mode: null, hide_mastered_questions: false };
        this.prefs$.next(fallback);
        return of(fallback);
      })
    );
  }

  updatePreferences(payload: PreferencesUpdatePayload): Observable<UserPreferences> {
      const sanitized = this.buildPayload(payload);
      if (!sanitized) {
        const cached = this.prefs$.getValue() ?? { preferred_scheine: null, theme_mode: null, hide_mastered_questions: false };
        return of(cached);
      }
    return this.http.put<UserPreferences>(`${this.apiUrl}/users/preferences.php`, sanitized).pipe(
      map((res) => this.normalizeResponse(res)),
      tap((normalized) => {
        this.prefs$.next(normalized);
        this.writeLocal(normalized);
      }),
      catchError((err) => {
        console.error('[Preferences] Speichern fehlgeschlagen', err);
        throw err;
      })
    );
  }

  private buildPayload(payload: PreferencesUpdatePayload): Record<string, unknown> | null {
    const sanitized: Record<string, unknown> = {};
    if (Object.prototype.hasOwnProperty.call(payload, 'preferred_scheine')) {
      sanitized['preferred_scheine'] = payload.preferred_scheine;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'theme_mode')) {
      sanitized['theme_mode'] = payload.theme_mode;
    }
    if (Object.prototype.hasOwnProperty.call(payload, 'hide_mastered_questions')) {
      sanitized['hide_mastered_questions'] = payload.hide_mastered_questions;
    }
    return Object.keys(sanitized).length ? sanitized : null;
  }

  private normalizeResponse(res: Partial<UserPreferences>): UserPreferences {
    return {
      preferred_scheine: Array.isArray(res?.preferred_scheine) ? res.preferred_scheine : null,
      theme_mode: this.normalizeThemeMode(res?.theme_mode ?? null),
      hide_mastered_questions: this.normalizeHideMastered(res?.hide_mastered_questions)
    };
  }

  private normalizeHideMastered(value: any): boolean {
    if (value === true) {
      return true;
    }
    if (value === false || value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      return lower === 'true' || lower === '1';
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    return false;
  }

  private normalizeThemeMode(mode: any): ThemeMode | null {
    if (mode === 'dark' || mode === 'light') {
      return mode;
    }
    return null;
  }

  private storageKey(): string | null {
    const userId = this.auth.getCurrentUser()?.id;
    if (!userId) return null;
    return `user-preferences:${userId}`;
  }

  private readLocal(): UserPreferences | null {
    try {
      const key = this.storageKey();
      if (!key || typeof localStorage === 'undefined') return null;
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          preferred_scheine: Array.isArray(parsed.preferred_scheine) ? parsed.preferred_scheine : null,
          theme_mode: this.normalizeThemeMode(parsed.theme_mode ?? null),
          hide_mastered_questions: this.normalizeHideMastered(parsed.hide_mastered_questions)
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  private writeLocal(prefs: UserPreferences): void {
    try {
      const key = this.storageKey();
      if (!key || typeof localStorage === 'undefined') return;
      localStorage.setItem(key, JSON.stringify({
        ...prefs,
        hide_mastered_questions: prefs.hide_mastered_questions
      }));
    } catch {
      // ignore
    }
  }
}
